<template>
  <div v-if="accountLocked" class="account-locked">
    <h1>This account is locked</h1>
    <p>{{ lockMessage }}</p>
  </div>
  <div v-else-if="lockChecked" id="app" style="height:100%">
    <a class="skip-link" href="#content-container">Skip to content</a>
    <p v-if="supportAccess" class="support-banner">Support access. Visitors still cannot open the live app.</p>
    <div ref="topbarMeasure" class="topbar-measure" inert aria-hidden="true">
        <span class="topbar-measure-brand">
          <b style="font-weight: 800; color: #e8e8e6; font-size: 0.85em; letter-spacing: -0.02em;">turbo</b><span style="font-weight: 300; color: #9ad12b; font-size: 0.85em;">hammer</span>
        </span>
        <span v-if="applicationLabel" class="topbar-measure-app">{{ applicationLabel }}</span>
        <span class="topbar-measure-nav">
          <span v-for="item in topbarSections" :key="item.id" class="topbar-link">
            <font-awesome-icon :icon="item.icon" class="topbar-nav-icon" />{{ item.label }}
          </span>
        </span>
        <span class="topbar-measure-actions">
          <span v-if="livePageUrl" class="topbar-action-btn">
            <font-awesome-icon icon="external-link-alt" />
            view live
          </span>
        </span>
      </div>
    <div id="topbar" ref="topbar" :class="{ 'topbar-ready': navReady, 'topbar-menu': menuMode, 'topbar-menu-open': menuOpen }">
      <a id="topbar-brand" :href="appListUrl" title="apps">
        <b style="font-weight: 800; color: #e8e8e6; font-size: 0.85em; letter-spacing: -0.02em;">turbo</b><span style="font-weight: 300; color: #9ad12b; font-size: 0.85em;">hammer</span>
      </a>
      <span v-if="applicationLabel" id="topbar-app" :title="applicationId">{{ applicationLabel }}</span>
      <nav v-if="!menuMode" id="topbar-nav" aria-label="Designer">
        <router-link v-for="item in topbarSections" :key="item.id" :to="getLink(item.id)" class="topbar-link" :aria-current="sectionCurrent(item.id)" :class="{ 'topbar-link-active': sectionActive(item.id) }">
          <font-awesome-icon :icon="item.icon" class="topbar-nav-icon" />{{ item.label }}
        </router-link>
      </nav>
      <div v-if="!menuMode" id="topbar-actions">
        <a v-if="livePageUrl" :href="livePageUrl" target="_blank" rel="noopener noreferrer" class="topbar-action-btn">
          <font-awesome-icon icon="external-link-alt" />
          view live
        </a>
      </div>
      <div v-else id="topbar-menu-wrap" ref="menuWrap">
        <button type="button" id="topbar-menu-btn" aria-label="Menu" aria-haspopup="true" :aria-expanded="menuOpen ? 'true' : 'false'" aria-controls="topbar-menu" @click="toggleTopbarMenu">
          <font-awesome-icon icon="bars" />
        </button>
        <nav v-if="menuOpen" id="topbar-menu" aria-label="Designer">
          <router-link v-for="item in topbarSections" :key="item.id" :to="getLink(item.id)" class="topbar-menu-link" :aria-current="sectionCurrent(item.id)" :class="{ 'topbar-link-active': sectionActive(item.id) }" @click="menuOpen = false">
            <font-awesome-icon :icon="item.icon" class="topbar-nav-icon" />{{ item.label }}
          </router-link>
          <template v-if="livePageUrl">
            <div class="topbar-menu-divider"></div>
            <a :href="livePageUrl" target="_blank" rel="noopener noreferrer" class="topbar-menu-link" @click="menuOpen = false">
              <font-awesome-icon icon="external-link-alt" class="topbar-nav-icon" />view live
            </a>
          </template>
        </nav>
      </div>
    </div>
    <div v-if="syncWarning" class="designer-sync-warning" role="alert">
      <span>{{ syncWarning }}</span>
      <button type="button" @click="dismissSyncWarning">Dismiss</button>
    </div>
    <main class="content" id="content-container" :style="style">
      <router-view :style="style" @page-changed="onDesignPageChanged"></router-view>
    </main>
    <div id="status" role="status">
      <span v-if="applicationId">{{ applicationId }}</span><span v-if="applicationId && currentSection"> · </span><span v-if="currentSection">{{ currentSection }}</span>
    </div>
  </div>
</template>

<script>
import { liveAppHref } from '../../../../shared/app-host.js'
import { designerSectionPath, readDesignerSelection, rememberDesignerSelection } from '../../lib/designer-selection.js'
import { clearDesignerWarning, onDesignerWarning, startDesignerSync, stopDesignerSync } from '../../lib/designer-sync.js'
import { TOPBAR_SECTIONS, topbarNeedsMenu } from '../../lib/topbar-nav.js'

export default {
  name: 'app',
  data () {

    console.log(`data method`)

    return {
      style: `height: ${window.innerHeight - 100}px`,
      msg: '',
      currentDesignPage: null,
      applicationTitle: '',
      accountLocked: false,
      lockChecked: false,
      lockMessage: 'This account is locked. Please contact support.',
      supportAccess: false,
      syncWarning: '',
      topbarSections: TOPBAR_SECTIONS,
      menuMode: false,
      menuOpen: false,
      navReady: false
    }
  },
  async mounted () {

    this.rememberOpenItem()
    await this.checkAccountLock()
    this.loadApplicationLabel()

    this._stopSyncWarning = onDesignerWarning((warning) => {

      this.syncWarning = warning
    })

    if (this.accountLocked) {

      return
    }

    this.joinDesignerSync()

    this._onWindowResize = () => {

      console.log(`window resized`)

      this.style = `height: ${window.innerHeight - 100}px`
      this.updateTopbarMode()
    }

    this._onTopbarPointerDown = (event) => {

      if (!this.menuOpen) return

      const wrap = this.$refs.menuWrap

      if (wrap && wrap.contains(event.target)) return

      this.menuOpen = false
    }

    this._onTopbarKeyDown = (event) => {

      if (event.key === 'Escape' && this.menuOpen) this.menuOpen = false
    }

    window.addEventListener('resize', this._onWindowResize)
    document.addEventListener('pointerdown', this._onTopbarPointerDown)
    document.addEventListener('keydown', this._onTopbarKeyDown)

    await this.$nextTick()
    this.bindTopbarObserver()
    this.updateTopbarMode()

    if (document.fonts && document.fonts.ready) {

      document.fonts.ready.then(() => {

        if (!this.accountLocked) this.updateTopbarMode()
      })
    }
  },
  watch: {
    '$route.params.application' () {

      this.loadApplicationLabel()
      this.joinDesignerSync()
    },
    '$route.path' () {

      this.menuOpen = false
      this.rememberOpenItem()

      if (!this.$route.path.includes('/design')) {

        this.currentDesignPage = null
      }
    },
    applicationLabel () {

      this.$nextTick(() => this.updateTopbarMode())
    },
    livePageUrl () {

      this.$nextTick(() => this.updateTopbarMode())
    }
  },
  computed: {
    appListUrl () {

      return `${window.__SUPERTURBO_API__}/#/apps`
    },
    applicationId () {

      return (this.$route.params && this.$route.params.application) || ''
    },
    applicationLabel () {

      const title = (this.applicationTitle || '').trim()

      return title || this.applicationId
    },
    currentSection () {

      const path = this.$route.path || ''
      const sections = ['design', 'automations', 'workflows', 'ai-build', 'ai-query', 'settings', 'analytics', 'menu-editor']

      for (const section of sections) {

        if (path.includes(`/${section}`)) return section.replace('-', ' ')
      }

      if (this.applicationId) return 'spreadsheets'

      return ''
    },
    livePageUrl () {

      if (!this.$route.path.includes('/design')) {

        return null
      }

      if (!this.currentDesignPage || !this.$route.params.application) {

        return null
      }

      const app = this.$route.params.application
      const page = this.currentDesignPage.home ? '' : this.currentDesignPage.uniqueTitle

      return liveAppHref(window.location, window.__SUPERTURBO_API__, app, page)
    }
  },
  beforeUnmount () {

    if (this._stopSyncWarning) this._stopSyncWarning()

    if (this._onWindowResize) window.removeEventListener('resize', this._onWindowResize)

    if (this._onTopbarPointerDown) document.removeEventListener('pointerdown', this._onTopbarPointerDown)

    if (this._onTopbarKeyDown) document.removeEventListener('keydown', this._onTopbarKeyDown)

    if (this._topbarObserver) this._topbarObserver.disconnect()

    stopDesignerSync()
  },
  methods: {
    bindTopbarObserver () {

      const topbar = this.$refs.topbar
      const measure = this.$refs.topbarMeasure

      if (!topbar || !measure || this._topbarObserver) return

      this._topbarObserver = new ResizeObserver(() => {

        this.updateTopbarMode()
      })
      this._topbarObserver.observe(topbar)
      this._topbarObserver.observe(measure)
    },
    updateTopbarMode () {

      const topbar = this.$refs.topbar
      const measure = this.$refs.topbarMeasure

      if (!topbar || !measure) {

        this.navReady = true
        return
      }

      const available = topbar.getBoundingClientRect().width
      const required = measure.getBoundingClientRect().width
      const needsMenu = topbarNeedsMenu(available, required)

      if (needsMenu !== this.menuMode) {

        this.menuMode = needsMenu

        if (!needsMenu) this.menuOpen = false
      }

      this.navReady = true
    },
    toggleTopbarMenu () {

      this.menuOpen = !this.menuOpen
    },
    joinDesignerSync () {

      if (this.accountLocked || !this.applicationId) return

      startDesignerSync(this.applicationId)
    },
    dismissSyncWarning () {

      clearDesignerWarning()
    },
    sectionActive (section) {

      const path = this.$route.path

      if (section === 'spreadsheets') {

        return path.includes('/spreadsheets') || (!path.includes('/design') && !path.includes('/automations') && !path.includes('/workflows') && !path.includes('/settings') && !path.includes('/analytics') && !path.includes('/ai-query') && !path.includes('/ai-build'))
      }

      return path.includes(`/${section}`)
    },
    sectionCurrent (section) {

      return this.sectionActive(section) ? 'page' : undefined
    },
    getLink (link) {

      const application = this.$route.params.application

      return designerSectionPath(application, link, readDesignerSelection(window.sessionStorage, application))
    },
    rememberOpenItem () {

      const params = this.$route.params || {}

      rememberDesignerSelection(window.sessionStorage, params.application, {
        spreadsheet: params.spreadsheet,
        page: params.page
      })
    },
    onDesignPageChanged (page) {

      this.currentDesignPage = page
    },
    async loadApplicationLabel () {

      const id = this.applicationId
      const requestId = id

      this.applicationTitle = ''

      if (!id) return

      try {

        const response = await fetch(`${window.__SUPERTURBO_API__}/application/${encodeURIComponent(id)}`, { credentials: 'include' })

        if (!response.ok || this.applicationId !== requestId) return

        const data = await response.json()

        if (this.applicationId !== requestId) return

        this.applicationTitle = (data && data.title) || ''
      } catch (error) {

        if (this.applicationId === requestId) this.applicationTitle = ''
      }
    },
    async checkAccountLock () {

      try {

        const response = await fetch(`${window.__SUPERTURBO_API__}/account-lock`, { credentials: 'include' })
        const data = await response.json()

        this.supportAccess = !!(data && data.locked && data.support)
        this.accountLocked = !!(data && data.locked) && !this.supportAccess

        if (data && data.message) {

          this.lockMessage = data.message
        }
      } catch (error) {

        this.accountLocked = false
        this.supportAccess = false
      }

      this.lockChecked = true
    }
  },
  components: {
  }
}
</script>

<style>

html, body {
  min-height: 100%;
  height: 100%;
  margin: 0;
  color-scheme: dark;
  scrollbar-color: #3a3a37 #181916;
}

#app {
  font-family: 'Helvetica Neue', 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #e8e8e6;
  background-color: #181916;
  height: 100%;
}

/* ── Top bar ── */
#topbar {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  height: 40px;
  background-color: #0f0f0d;
  border-bottom: 1px solid #2a2a27;
  padding: 0 12px;
  gap: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  min-width: 0;
}
#topbar:not(.topbar-ready) {
  overflow: hidden;
}
#topbar.topbar-menu-open {
  z-index: 400;
}

.topbar-measure {
  position: fixed;
  top: 0;
  right: 100%;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  width: max-content;
  height: 40px;
  padding: 0 12px;
  gap: 16px;
  white-space: nowrap;
  visibility: hidden;
  pointer-events: none;
}
.topbar-measure-brand {
  flex-shrink: 0;
  padding-right: 12px;
  border-right: 1px solid #2a2a27;
}
.topbar-measure-app {
  flex-shrink: 0;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8rem;
  font-weight: 600;
}
.topbar-measure-nav {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
.topbar-measure-actions {
  flex-shrink: 0;
}

#topbar-brand {
  flex-shrink: 0;
  padding-right: 12px;
  border-right: 1px solid #2a2a27;
  cursor: pointer;
  text-decoration: none;
}
#topbar-brand:hover {
  opacity: 0.8;
}

#topbar-app {
  flex-shrink: 0;
  min-width: 0;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8rem;
  font-weight: 600;
  color: #e8e8e6;
}
#topbar.topbar-menu #topbar-app {
  flex-shrink: 1;
}

#topbar-nav {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 2px;
  flex: 1 0 auto;
  min-width: 0;
}

.topbar-link {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #b0b0ae;
  text-decoration: none !important;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s;
}
.topbar-link:hover {
  background-color: #1e1e1b;
  color: #b0b0ae;
  text-decoration: none;
}
.topbar-link-active {
  background-color: #1a1a17 !important;
  color: #e8e8e6 !important;
}

.topbar-nav-icon {
  font-size: 0.72rem;
  opacity: 0.6;
}
.topbar-link-active .topbar-nav-icon {
  opacity: 1;
  color: #9ad12b;
}

#topbar-actions {
  margin-left: auto;
  flex-shrink: 0;
}

#topbar-menu-wrap {
  position: relative;
  margin-left: auto;
  flex-shrink: 0;
}

#topbar-menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  margin: 0;
  padding: 0;
  border-radius: 5px;
  border: 1px solid #2a2a27;
  background: transparent;
  color: #b0b0ae;
  cursor: pointer;
  font-size: 0.9rem;
  line-height: 1;
  appearance: none;
}
#topbar-menu-btn:hover,
#topbar-menu-btn[aria-expanded="true"] {
  background-color: #1e1e1b;
  color: #e8e8e6;
  border-color: #3a3a37;
}
#topbar-menu-btn:focus-visible {
  outline: 2px solid #9ad12b;
  outline-offset: 2px;
}

#topbar-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 200px;
  max-width: calc(100vw - 24px);
  background: #141412;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  padding: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  z-index: 1;
}

.topbar-menu-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 5px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #b0b0ae;
  text-decoration: none !important;
  white-space: nowrap;
}
.topbar-menu-link:hover {
  background-color: #1e1e1b;
  color: #e8e8e6;
}
.topbar-menu-link:focus-visible {
  outline: 2px solid #9ad12b;
  outline-offset: -2px;
}
.topbar-menu-link.topbar-link-active {
  background-color: #1a1a17;
  color: #e8e8e6;
}

.topbar-menu-divider {
  height: 1px;
  margin: 4px 6px;
  background: #2a2a27;
}

.topbar-action-btn {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 0.78rem;
  color: #b0b0ae;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  border: 1px solid #2a2a27;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.topbar-action-btn:hover {
  background-color: #1e1e1b;
  color: #b0b0ae;
  border-color: #3a3a37;
}

/* ── Context action bar (per page: "new spreadsheet" etc.) ── */
#context-nav,
.navbar#context-nav {
  background-color: #141412 !important;
  border-bottom: 1px solid #2a2a27 !important;
  min-height: 38px;
  padding: 4px 10px;
  flex-wrap: wrap;
}

/* Make Bootstrap navbar buttons inside context-nav look clean */
#context-nav .btn {
  font-size: 0.78rem;
  min-height: 24px;
  padding: 4px 10px;
  border-radius: 4px;
  border-color: #333330;
  background-color: transparent;
  color: #909090;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
}
#context-nav .btn:hover {
  background-color: #222220;
  color: #c0c0be;
  border-color: #484845;
}

/* Make Bootstrap nav-link text inside context-nav visible */
#context-nav .navbar-collapse {
  flex: 1 1 auto;
  min-width: 0;
}
#context-nav .navbar-nav {
  flex-wrap: wrap;
  row-gap: 2px;
}
#context-nav .nav-link {
  font-size: 0.78rem;
  color: #b0b0ae;
  padding: 4px 10px;
  transition: color 0.1s;
}
#context-nav .nav-link:hover {
  color: #c0c0be;
}
#context-nav .nav-link.active,
#context-nav .nav-item.active > .nav-link {
  color: #e8e8e6;
  box-shadow: inset 0 -2px 0 #9ad12b;
}

#context-nav .btn.context-nav-danger {
  color: #f0a8b4;
  border-color: #8a3a4a;
  background-color: transparent;
}
#context-nav .btn.context-nav-danger:hover {
  color: #fff;
  background-color: #6a2434;
  border-color: #f82471;
}

.designer-sync-warning {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #3a2a12;
  color: #f3d7a1;
  border-bottom: 1px solid #6a4a20;
  padding: 8px 12px;
  font-size: 0.85rem;
}

.designer-sync-warning button {
  flex: none;
  background: transparent;
  color: #f3d7a1;
  border: 1px solid #6a4a20;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
}

/* ── Status bar ── */
#status {
  position: fixed;
  bottom: 0;
  width: 100%;
  border-top: 1px #1e1e1b solid;
  background: #0a0a08;
  color: #b0b0ae;
  font-size: 0.72rem;
  height: 22px;
  line-height: 22px;
  padding: 0 10px;
  z-index: 1000;
}

/* ── Router view area ── */
#content-container {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

#content-container > * {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

a {
  color: #e8e8e6;
}

.account-locked {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #181916;
  color: #e8e8e6;
  text-align: center;
  padding: 24px;
}

.account-locked h1 {
  margin: 0 0 8px;
  font-size: 1.6rem;
}

.account-locked p {
  margin: 0;
  color: #a3a89a;
}

.skip-link {
  position: absolute;
  left: 8px;
  top: -48px;
  z-index: 2000;
  background: #fff;
  color: #111;
  padding: 8px 12px;
  border-radius: 4px;
}
.skip-link:focus {
  top: 8px;
}

.support-banner {
  margin: 0;
  padding: 8px 16px;
  background: #3a3418;
  color: #e0b15a;
  text-align: center;
}

</style>
